import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

export default function ArtworkStage({
  src,
  alt = "",
  videoOpen = false,
  videoSrc,
  onCloseVideo,
  height = "100%",
  children,
  centerVideo = false,
  videoVisible,
}) {
  // Handles API values: 1, "1", true, 0, "0", false.
  const isVideoVisible =
    videoVisible === true ||
    videoVisible === 1 ||
    videoVisible === "1";

  const showMobileVideo = centerVideo && videoOpen;
  const showDesktopVideo = !centerVideo && videoOpen;
  const showUnavailable = !isVideoVisible || !videoSrc;

  const unavailableContent = (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 1,
        px: 1.5,
        py: 1,
        bgcolor: "#171717",
        color: "#fff",
        textAlign: "center",
      }}
    >
      <PlayCircleOutlineIcon sx={{ fontSize: 32, color: "#bfa16a" }} />
      <Typography
        component="h3"
        sx={{
          m: 0,
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.8px",
          color: "#fff",
        }}
      >
        LIVE STREAM UNAVAILABLE
      </Typography>
      <Typography sx={{ fontSize: "0.68rem", lineHeight: 1.4, color: "#bdbdbd" }}>
        The livestream is currently unavailable. Please check again later.
      </Typography>
    </Box>
  );

  const renderVideo = () => {
    if (showUnavailable) return unavailableContent;

    return (
      <Box
        component="iframe"
        key={videoSrc}
        title="Leon Gallery Live Stream"
        src={videoSrc}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          minHeight: 0,
          border: 0,
          bgcolor: "#171717",
        }}
      />
    );
  };

  return (
    <Box
      component="section"
      aria-label="Artwork and livestream"
      sx={{
        position: "relative",
        width: "100%",
        height,
        // Keep the stage visible on mobile even when its parent only uses height: 100%.
        minHeight: { xs: 220, md: 0 },
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      {showMobileVideo ? (
        <>
          {/* MOBILE: livestream is the large/main view */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              minHeight: 220,
              overflow: "hidden",
              bgcolor: "#171717",
            }}
          >
            {renderVideo()}
          </Box>

          {/* MOBILE: smaller artwork preview overlay on the left */}
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              width: { xs: "14%", sm: "12%" },
              maxWidth: 80,
              minWidth: 50,
              aspectRatio: "3 / 4",
              zIndex: 10,
              overflow: "hidden",
              bgcolor: "#fff",
              border: "1px solid rgba(191,161,106,0.9)",
              boxShadow: "0 4px 18px rgba(0,0,0,0.4)",
            }}
          >
            <Box
              component="img"
              src={src}
              alt={alt}
              sx={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "contain",
                bgcolor: "#fff",
              }}
            />
          </Box>

          {typeof onCloseVideo === "function" && (
            <IconButton
              onClick={onCloseVideo}
              aria-label="Close video"
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                zIndex: 11,
                width: 32,
                height: 32,
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.7)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 0,
                "&:hover": { bgcolor: "rgba(0,0,0,0.9)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ) : (
        <>
          {/* MAIN ARTWORK */}
          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              minHeight: 0,
              objectFit: "contain",
              bgcolor: "#fff",
            }}
          />

          {/* DESKTOP: livestream remains a small overlay over artwork */}
          {showDesktopVideo && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                width: { md: "42%", lg: "38%" },
                aspectRatio: "16 / 9",
                maxWidth: 440,
                minWidth: 220,
                zIndex: 10,
                overflow: "hidden",
                border: "1px solid rgba(191,161,106,0.75)",
                boxShadow: "0 4px 18px rgba(0,0,0,0.35)",
                bgcolor: "#171717",
              }}
            >
              {renderVideo()}
              {typeof onCloseVideo === "function" && (
                <IconButton
                  onClick={onCloseVideo}
                  aria-label="Close video"
                  sx={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    zIndex: 5,
                    width: 30,
                    height: 30,
                    color: "#fff",
                    bgcolor: "rgba(0,0,0,0.7)",
                    borderRadius: 0,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.9)" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </>
      )}

      {/* Existing overlays, such as SOLD */}
      {children}
    </Box>
  );
}
